import { Controller, useForm } from 'react-hook-form';

import { useEffect, useMemo } from 'react';

import { styled } from '@mui/material';

import { useParams } from 'react-router';

import { ClassValidatorResolver } from '@app-velocity/validators';
import { Form, Select } from '@app-velocity/ui';
import { useI18n } from '@app-velocity/i18n';

import { useUpdateScenarioMutation } from '@app-velocity/modules/scenarios';
import { TPrototypeDto } from '@app-velocity/av-api-v2';

import { ReportSettingsDto } from './dto/ReportSettingsDto';

const StyledSettings = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  overflow: 'auto',
  flex: '0 0 40%',
}));

export type ReportSettingsProps = {
  prototype: TPrototypeDto;
};

export const ReportSettings = ({ prototype }: ReportSettingsProps) => {
  const { t } = useI18n();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const { watch, control, reset } = useForm<ReportSettingsDto>({
    resolver: ClassValidatorResolver(ReportSettingsDto),
    defaultValues: {
      category: prototype.category,
      journey: prototype.journey,
    },
  });

  const {
    mutate: updatePrototype,
    isPending,
    isSuccess,
    data,
  } = useUpdateScenarioMutation(workspaceId, prototype.id);

  useEffect(() => {
    if (!isSuccess) return;

    reset({
      category: data?.category,
      journey: data?.journey,
    });
  }, [isSuccess, data]);

  const [category, journey] = watch(['category', 'journey']);

  const categoryOptions = [
    {
      label: t(
        'prototypes:journey_heuristics.filters.product_category.activation'
      ),
      value: 'Activation',
    },
    {
      label: t(
        'prototypes:journey_heuristics.filters.product_category.retention'
      ),
      value: 'Retention',
    },
    {
      label: t(
        'prototypes:journey_heuristics.filters.product_category.expansion'
      ),
      value: 'Expansion',
    },
  ];

  const getJourneysForCategory = (category: string | null) => {
    if (!category) return [];

    switch (category) {
      case 'Activation': {
        return [
          {
            label: t(
              'prototypes:journey_heuristics.filters.journey_name.time_to_first_value'
            ),
            value: 'Time to first value',
          },
        ];
      }

      case 'Retention': {
        return [
          {
            label: t(
              'prototypes:journey_heuristics.filters.journey_name.navigation_and_wayfinding'
            ),
            value: 'Navigation & wayfinding',
          },
          {
            label: t(
              'prototypes:journey_heuristics.filters.journey_name.task_and_workflow_management'
            ),
            value: 'Task & workflow management',
          },
          {
            label: t(
              'prototypes:journey_heuristics.filters.journey_name.output_and_execution'
            ),
            value: 'Output & execution',
          },
        ];
      }

      case 'Expansion': {
        return [
          {
            label: t(
              'prototypes:journey_heuristics.filters.journey_name.invite_a_team_mate'
            ),
            value: 'Invite a team mate',
          },
          {
            label: t(
              'prototypes:journey_heuristics.filters.journey_name.upgrade_plan_higher'
            ),
            value: 'Upgrade Plan higher',
          },
        ];
      }

      default: {
        return [];
      }
    }
  };

  const journeyOptions: {
    label: string;
    value: string;
  }[] = useMemo(() => {
    return getJourneysForCategory(category);
  }, [category]);

  const updateCategory = (category: string) => {
    const journeys = getJourneysForCategory(category);

    updatePrototype({
      category,
      journey: journeys[0].value || '',
    });
  };

  const updateJourney = (journey: string) => {
    updatePrototype({
      journey,
    });
  };

  return (
    <StyledSettings>
      <Form.FieldContainer>
        <Controller
          name="category"
          control={control}
          render={({ field }) => (
            <Select
              disabled={isPending}
              displayEmpty
              {...field}
              onChange={(e) => {
                field.onChange(e);
                updateCategory(e.target.value as string);
              }}
              value={category}
              variant="outlined"
              size="extra-small"
              renderValue={(val) => {
                if (!val)
                  return t('prototypes:journey_heuristics.select_category');

                return val as string;
              }}
            >
              {categoryOptions.map((option) => (
                <Select.Item key={option.value} value={option.value}>
                  {option.label}
                </Select.Item>
              ))}
            </Select>
          )}
        />
      </Form.FieldContainer>
      {category ? (
        <Form.FieldContainer>
          <Controller
            name="journey"
            control={control}
            render={({ field }) => (
              <Select
                disabled={isPending}
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  updateJourney(e.target.value as string);
                }}
                displayEmpty
                value={journey}
                variant="outlined"
                size="extra-small"
                renderValue={(val) => {
                  if (!val)
                    return t('prototypes:journey_heuristics.select_journey');

                  return val as string;
                }}
              >
                {journeyOptions.map((option) => (
                  <Select.Item key={option.value} value={option.value}>
                    {option.label}
                  </Select.Item>
                ))}
              </Select>
            )}
          />
        </Form.FieldContainer>
      ) : null}
    </StyledSettings>
  );
};
